const getBaseUrl = () => {
  return process.env.REACT_APP_API_BASE_URL;
};

const getAuthHeaders = (isFormData = false) => {
  const token = localStorage.getItem("token");
  const headers = {};
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

const handleApiResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const errorMsg =
      (typeof result === "object" && (result?.message || result?.error)) ||
      (typeof result === "string" ? result : "Request failed");
    throw new Error(errorMsg);
  }

  return result;
};

/**
 * Supported deep-link app routes for notifications
 */
export const NOTIFICATION_REDIRECT_ROUTES = [
  { label: "Home (Default)", value: "/home", icon: "🏠" },
  { label: "Leaderboard", value: "/leaderboard", icon: "🏆" },
  { label: "All Games", value: "/game", icon: "🎮" },
  { label: "Tap Rush Game", value: "/tap-rush", icon: "👆" },
  { label: "777 Lucky Slot Game", value: "/lucky-slot", icon: "🎰" },
  { label: "Lucky Spinner Game", value: "/spinner", icon: "🎡" },
  { label: "Boom Cards Game", value: "/boom-cards", icon: "🃏" },
  { label: "Refer & Earn", value: "/refer", icon: "👥" },
  { label: "Zeta Journey", value: "/zeta-journey", icon: "🗺️" },
  { label: "Account / Profile", value: "/account", icon: "👤" },
  { label: "KYC Verification", value: "/kyc", icon: "🆔" },
  { label: "Announcement", value: "/announcement", icon: "📢" },
];

/**
 * List event notifications with pagination, filters and search
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.limit=10]
 * @param {string} [params.eventId]
 * @param {number|string} [params.userId]
 * @param {string} [params.type] - "all" | "event_winner_push" | "event_reward" | "event_notification"
 * @param {string} [params.search]
 */
export const getEventNotifications = async ({
  page = 1,
  limit = 10,
  eventId = "",
  userId = "",
  type = "all",
  search = "",
} = {}) => {
  const baseUrl = getBaseUrl();
  const queryParams = new URLSearchParams();
  if (page) queryParams.append("page", page);
  if (limit) queryParams.append("limit", limit);
  if (eventId && eventId !== "all") queryParams.append("eventId", eventId);
  if (userId) queryParams.append("userId", userId);
  if (type && type !== "all") queryParams.append("type", type);
  if (search && search.trim()) queryParams.append("search", search.trim());

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const url = `${baseUrl}/event-notification/list${queryString}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleApiResponse(response);
};

/**
 * Fetch a single event notification record by ID
 * @param {number|string} id
 */
export const getEventNotificationById = async (id) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event-notification/view/${encodeURIComponent(id)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleApiResponse(response);
};

/**
 * Create a new event notification
 * Supports both FormData (with file) or raw JSON object
 * @param {FormData|Object} data
 */
export const createEventNotification = async (data) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event-notification/create`;
  const isFormData = data instanceof FormData;

  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(isFormData),
    body: isFormData ? data : JSON.stringify(data),
  });

  return handleApiResponse(response);
};

/**
 * Edit an existing event notification
 * @param {number|string} id
 * @param {FormData|Object} data
 */
export const updateEventNotification = async (id, data) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event-notification/edit/${encodeURIComponent(id)}`;
  const isFormData = data instanceof FormData;

  const response = await fetch(url, {
    method: "PUT",
    headers: getAuthHeaders(isFormData),
    body: isFormData ? data : JSON.stringify(data),
  });

  return handleApiResponse(response);
};

/**
 * Delete an event notification
 * @param {number|string} id
 */
export const deleteEventNotification = async (id) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event-notification/delete/${encodeURIComponent(id)}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  return handleApiResponse(response);
};

/**
 * Dispatch Push Notification via FCM
 * @param {number|string} id
 */
export const dispatchPushNotification = async (id) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event-notification/send-notification/${encodeURIComponent(id)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleApiResponse(response);
};

/**
 * Send an immediate test push notification directly using an FCM token
 * @param {Object} payload
 * @param {string} payload.fcmToken
 * @param {string} payload.title
 * @param {string} payload.message
 * @param {string} [payload.redirectRoute="/home"]
 * @param {string} [payload.eventId]
 */
export const sendTestPushNotification = async (payload) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event-notification/test-notification`;

  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(false),
    body: JSON.stringify(payload),
  });

  return handleApiResponse(response);
};
